
























































/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call */
import {
  defineComponent, ref, computed, watch, onBeforeUnmount, getCurrentInstance,
  PropType,
} from '@vue/composition-api';

import {
  BuyEquityResponseType, PackageBuyStatusEnum, SuperMemberSourceEnum,
  RightPackageTypeEnum, SuperMemberSceneEnum, SuperCardSummaryBackType,
} from '@/api/rights/types';
import { openInnerPage } from '@/api/native/utils';
import URLUtils from '@/shared/utils/URLUtils';
import { MembersRightsPrefix } from '@/constant';
import store from '@/store';
import { ForceReadService, ForceReadServiceReturnType } from '@/widgets/right-contract';
import { Radio } from '@hb/coffee';
import { RightArrowOutline12 } from '@hb/coffee-icons';

import {
  buildTqParams,
  reportAgreementCheck,
  reportAgreement,
} from './tianqi';
import '@/views_v2/pre-apply-choose/store';

const notBuyStatus = PackageBuyStatusEnum.NOT_BUY;

export default defineComponent({
  name: 'SuperMemberCardComposition',
  components: {
    Radio,
    RightArrowOutline12,
  },
  props: {
    commoditys: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    from: {
      type: String as PropType<SuperMemberSourceEnum>,
      required: true,
    },
    sceneType: {
      type: Number as PropType<SuperMemberSceneEnum>,
      default: () => SuperMemberSceneEnum.FQ_MALL_AGREEMENT_SCENE,
    },
    /** 概要数据与购买状态由 entry 请求后注入，组件仅做展示 */
    rightInfo: {
      type: Object as PropType<BuyEquityResponseType | SuperCardSummaryBackType | null>,
      default: null,
    },
    buyStatus: {
      type: Number as PropType<PackageBuyStatusEnum | null>,
      default: null,
    },
    /** 由 entry 注入，用于从详情页返回时重新拉取概要 */
    onRefresh: {
      type: Function as PropType<() => void>,
      default: null,
    },
  },
  setup(props) {
    const checked = ref(false);
    const preChooseContract = ref<ForceReadServiceReturnType | null>(null);
    const currentPackageType = ref<RightPackageTypeEnum | null>(null);
    const visibilityCheckHandlerRef = ref<(() => void) | null>(null);
    const instance = getCurrentInstance();

    const tqParams = computed(() => buildTqParams(
      (props.rightInfo || null) as Record<string, unknown> | null,
      props.from,
      props.commoditys,
    ));

    const rightContractInfo = computed(() => props.rightInfo?.contractInfo);
    const hasContractInfo = computed(
      () => rightContractInfo.value && Object.keys(rightContractInfo.value).length > 0,
    );
    const displayTitle = computed(
      () => props.rightInfo?.title || '成为会员 立享返现红包权益',
    );

    const purchaseDesc = computed(() => {
      const info = props.rightInfo;
      if (!info) return null;
      const { packageType, originalPrice = '' } = info;
      const { sellPrice } = info;
      if (packageType === RightPackageTypeEnum.SUPER_MEMBER_CARD) {
        return sellPrice ? `自动续费${String(sellPrice)}元/月可随时取消` : null;
      }
      if (packageType === RightPackageTypeEnum.SUPER_MEMBER_PLUS_CARD) {
        return sellPrice
          ? `超级会员付费${String(sellPrice)}元升级PLUS会员 后续自动续费${String(originalPrice)}元/月`
          : null;
      }
      return null;
    });

    /** 根据 rightInfo 同步创建/更新 ForceReadService，仅视图与协议强读逻辑，无请求 */
    watch(
      () => props.rightInfo,
      (info) => {
        if (!info?.packageType) {
          if (!preChooseContract.value) {
            preChooseContract.value = ForceReadService({
              packageType: RightPackageTypeEnum.SUPER_MEMBER_CARD,
              forceReadFromScene: SuperMemberSourceEnum.CREDIT_AGREEMENT,
            });
            currentPackageType.value = RightPackageTypeEnum.SUPER_MEMBER_CARD;
          }
          return;
        }
        const { packageType } = info;
        if (!preChooseContract.value || currentPackageType.value !== packageType) {
          if (preChooseContract.value) {
            preChooseContract.value.destroyInstance();
          }
          preChooseContract.value = ForceReadService({
            packageType,
            forceReadFromScene: SuperMemberSourceEnum.CREDIT_AGREEMENT,
          });
          currentPackageType.value = packageType;
        }
      },
      { immediate: true },
    );

    async function switchCheck() {
      checked.value = !checked.value;
      if (checked.value) {
        reportAgreementCheck(instance?.proxy, tqParams.value);
        const contract = preChooseContract.value;
        if (contract) {
          const res = await contract.handleForceRead({
            contractScene: props.rightInfo?.contractInfo?.scene || '',
            tqParams: (tqParams.value as unknown) as Record<string, unknown>,
          });
          checked.value = res;
        }
      }
    }

    function showContract() {
      const { contractInfo } = props.rightInfo || {};
      if (contractInfo && preChooseContract.value) {
        reportAgreement(instance?.proxy, tqParams.value);
        preChooseContract.value.handleForceRead({
          contractScene: contractInfo.scene,
          tqParams: (tqParams.value as unknown) as Record<string, unknown>,
          preview: true,
        }).then((handleRes: boolean) => {
          if (handleRes) {
            checked.value = handleRes;
          }
        });
      }
    }

    function goDetail() {
      const url = URLUtils.formatUrl(
        `${MembersRightsPrefix}super-member.html#/super-member`,
        {
          from: `${String(props.from)}_cjhy_detail`,
          packageId: props.rightInfo?.packageId,
          packageType: props.rightInfo?.packageType,
          hiddenNavigationBar: 1,
        },
      );
      openInnerPage(url);
      const handler = () => {
        if (props.onRefresh) props.onRefresh();
      };
      visibilityCheckHandlerRef.value = handler;
      document.addEventListener('visibilitychange', handler);
    }

    onBeforeUnmount(() => {
      if (visibilityCheckHandlerRef.value) {
        document.removeEventListener('visibilitychange', visibilityCheckHandlerRef.value);
      }
      if (preChooseContract.value) {
        preChooseContract.value.destroyInstance();
      }
    });

    return {
      notBuyStatus,
      buyStatus: computed(() => props.buyStatus),
      rightInfo: computed(() => props.rightInfo),
      checked,
      preChooseContract,
      rightContractInfo,
      hasContractInfo,
      displayTitle,
      purchaseDesc,
      switchCheck,
      showContract,
      goDetail,
    };
  },
});
